import React, { useMemo, useState } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import { Button } from "components/Form";
import IdConnectedList from "components/IdConnectedList";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import MessageBox from "components/MessageBox";
import { ReactSelect } from "components/SearchableSelector";
import SearchInputTable from "components/SearchInput";

import { ClearingSaleBuyerStatus } from "constants/clearingSaleAttributes";
import { ApiModel } from "constants/loading";

import { isWholePositiveNumber } from "lib/math";
import { openImportAuctionsPlusCSV } from "lib/navigation";

import {
  getCurrentSale,
  getKeywordLookups,
  selectAlphabeticalBuyerIds,
  selectBidderNumbersByBuyerIdLookup,
  selectBusinessIdByRegistrationNumberLookup,
  selectBuyerStatusByBuyerIdLookup,
} from "selectors";

import { BuyerCard } from "./BuyerCard";
import {
  ALL_STATUSES,
  getStatusFilter,
  statusFilterValues,
  type StatusFilterValue,
} from "./buyerFilterPolicy";
import {
  getSortComparator,
  SortFields,
  type ValuesByBuyerId,
} from "./buyerSortPolicy";

const FilterSelector = styled(ReactSelect)`
  width: 220px;
`;

const FilterRow = styled(Row)(
  ({ theme }) => `
  gap: ${theme.space[2]}px;
`,
);

// SearchInputTable doesn't forward className, so it can't be widened via styled(); pass this via its `style` prop instead.
const SEARCH_BOX_STYLE = { flex: "2 1 260px" };

const STATUS_FILTER_LABELS: Record<StatusFilterValue, string> = {
  [ALL_STATUSES]: "All Statuses",
  ...(Object.fromEntries(
    Object.values(ClearingSaleBuyerStatus).map(status => [status, status]),
  ) as Record<ClearingSaleBuyerStatus, string>),
  // The Sold status covers buyers who haven't paid yet, so make that explicit in the filter.
  [ClearingSaleBuyerStatus.SOLD]: "Sold/Unpaid",
};

const statusFilterOptions = statusFilterValues.map(value => ({
  label: STATUS_FILTER_LABELS[value],
  value,
}));

const BUYER_NAME_SORT_OPTION = {
  label: "Buyer (A-Z)",
  value: SortFields.BUYER_NAME,
};
const STATUS_SORT_OPTION = {
  label: "Status (Sold to Paid)",
  value: SortFields.STATUS,
};
const BIDDER_NUMBER_SORT_OPTION = {
  label: "Bidder Number (1-99)",
  value: SortFields.BIDDER_NUMBER,
};

function EmptyBuyers() {
  return <MessageBox>No Lots sold yet.</MessageBox>;
}

function NoMatchingBuyers() {
  return <MessageBox>No buyers match the current filters.</MessageBox>;
}

export const BuyersComponent = () => {
  const sale = useSelector(getCurrentSale);

  const buyerIds = useSelector(selectAlphabeticalBuyerIds);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(statusFilterOptions[0]);

  const businessKeywordLookup = useSelector(getKeywordLookups).businesses;
  const isUsingRegisteredBidders =
    useSelector(getCurrentSale)?.using_registered_bidders;
  const businessIdByRegistrationNumberLookup = useSelector(
    selectBusinessIdByRegistrationNumberLookup as (
      state: RootState,
    ) => Record<string, string>,
  );
  const buyerStatusByBuyerId = useSelector(
    selectBuyerStatusByBuyerIdLookup as (
      state: RootState,
    ) => Record<string, ClearingSaleBuyerStatus>,
  );
  const bidderNumbersByBuyerId = useSelector(
    selectBidderNumbersByBuyerIdLookup as (state: RootState) => ValuesByBuyerId,
  );
  const sortOptions = useMemo(
    () => [
      BUYER_NAME_SORT_OPTION,
      STATUS_SORT_OPTION,
      ...(isUsingRegisteredBidders ? [BIDDER_NUMBER_SORT_OPTION] : []),
    ],
    [isUsingRegisteredBidders],
  );
  // Bidder sales are primarily worked from bidder number, so default to that; other sales default to buyer name.
  const [sortBy, setSortBy] = useState(
    isUsingRegisteredBidders
      ? BIDDER_NUMBER_SORT_OPTION
      : BUYER_NAME_SORT_OPTION,
  );

  const searchPlaceholder = isUsingRegisteredBidders
    ? "Bidder number/Buyer"
    : "Quicksearch";

  const textMatchedIds = useMemo(() => {
    const trimmedSearchText = searchText.trim();
    if (isUsingRegisteredBidders && isWholePositiveNumber(trimmedSearchText)) {
      return buyerIds.filter(
        businessId =>
          businessId ===
          businessIdByRegistrationNumberLookup[trimmedSearchText],
      );
    } else {
      const lowerSearchText = trimmedSearchText.toLowerCase();
      const matchingBusinessIdsLookup = Object.keys(
        businessKeywordLookup,
      ).reduce((acc, lookupToken) => {
        if (lookupToken.includes(lowerSearchText)) {
          businessKeywordLookup[lookupToken].forEach(b => {
            acc[b.business.id] = true;
          });
        }
        return acc;
      }, {});

      return buyerIds.filter(id => matchingBusinessIdsLookup[id]);
    }
  }, [
    isUsingRegisteredBidders,
    searchText,
    buyerIds,
    businessIdByRegistrationNumberLookup,
    businessKeywordLookup,
  ]);

  const sortedIds = useMemo(() => {
    const statusFilterFn = getStatusFilter(statusFilter.value);
    const filteredIds = textMatchedIds.filter(id =>
      statusFilterFn(buyerStatusByBuyerId[id]),
    );
    const comparator = getSortComparator(
      sortBy.value,
      { buyerStatusByBuyerId, bidderNumbersByBuyerId },
      filteredIds,
    );
    return comparator ? [...filteredIds].sort(comparator) : filteredIds;
  }, [
    textMatchedIds,
    statusFilter,
    sortBy,
    buyerStatusByBuyerId,
    bidderNumbersByBuyerId,
  ]);

  return (
    <Column>
      <FilterRow padding={1} flexWrap alignCenter printHidden>
        <SearchInputTable
          handleChange={setSearchText}
          value={searchText}
          placeholder={searchPlaceholder}
          style={SEARCH_BOX_STYLE}
        />
        <FilterSelector
          value={statusFilter}
          options={statusFilterOptions}
          onChange={setStatusFilter}
        />
        <FilterSelector
          value={sortBy}
          options={sortOptions}
          onChange={setSortBy}
        />
      </FilterRow>
      <Row padding={1}>
        <Button
          data-tour="importAuctionsPlusResults"
          onClick={() => openImportAuctionsPlusCSV(sale)}
        >
          Import AuctionsPlus Results
        </Button>
      </Row>
      <IdConnectedList
        component={BuyerCard}
        orderedIds={sortedIds}
        emptyComponent={buyerIds.length ? NoMatchingBuyers : EmptyBuyers}
      />
    </Column>
  );
};

// Wait For Sync Wrapper
export const Buyers = () => {
  return (
    <WaitForSync
      requiredData={[
        ApiModel.SALE_LOTS,
        ApiModel.CONSIGNMENTS,
        ApiModel.BUSINESSES,
        ApiModel.BIDDERS,
      ]}
    >
      <BuyersComponent />
    </WaitForSync>
  );
};
